import { useState } from 'react';
import { useIntl } from '@leagueplatform/locales';
import { useNavigate } from 'react-router-dom';
import { IframeModal } from 'components/modals/common/iframe-modal.component';
import { APP_MANAGED_ROUTES } from 'common/app-managed-routes';
import { parseValidHttpsUrl } from 'common/parse-valid-https-url';
import {
  getAllAppointmentIframeAllowedOrigins,
  isMockAppointmentIframeEnabled,
} from 'common/env';
import { QUERY_PARAM_KEYS } from 'common/constants';
import { useUrlParam } from 'hooks/use-url-param/use-url-param.hook';
import { useIframeKeepAlive } from 'hooks/use-iframe-keep-alive/use-iframe-keep-alive.hook';

const MOCK_CANCEL_IFRAME_PATH = '/mock-cancel-appointment-iframe.html';

function getValidatedCancelSrc(src: string): string | undefined {
  const url = parseValidHttpsUrl(src);
  if (!url) return undefined;
  const { origin } = new URL(url);
  return getAllAppointmentIframeAllowedOrigins().includes(origin)
    ? url
    : undefined;
}

/**
 * Renders the iframe modal when a valid HTTPS `cancelUrl` query param is
 * present on the appointment details page.
 */
export const CancelAppointmentModalHost = () => {
  const navigate = useNavigate();
  const { formatMessage } = useIntl();
  const { urlParam } = useUrlParam(QUERY_PARAM_KEYS.CANCEL_URL, true);
  const [isOpen, setIsOpen] = useState(true);

  useIframeKeepAlive({ enabled: !!urlParam && isOpen });

  const handleClose = () => {
    setIsOpen(false);
    navigate(APP_MANAGED_ROUTES.APPOINTMENTS, { replace: true });
  };

  if (!urlParam) return null;

  const iframeSrc = isMockAppointmentIframeEnabled()
    ? MOCK_CANCEL_IFRAME_PATH
    : getValidatedCancelSrc(urlParam);

  if (!iframeSrc) return null;

  return (
    <IframeModal open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <iframe
        id="cancelAppointmentIframeModal"
        height="100%"
        width="100%"
        title={formatMessage({ id: 'STR_CANCEL_APPOINTMENT' })}
        src={iframeSrc}
      />
    </IframeModal>
  );
};
